// Copyright (c) Docugami, Inc. All rights reserved.

import { Shimmer } from "@fluentui/react";
import { useDocumentDgml } from "hooks/documentDgml";
import { useDrop } from "react-dnd";
import { DndChunk, DndType } from "./DndTypes";
import { ActiveDropTargetStyle, LinkableClassName, DisabledClassName, ItemSlot, ItemSlotStyles, SelectableClassName, UnconfirmedClassName, ChunkSlotItem, ItemSlotProps, ReportChunkSlotItem } from "./ItemSlot";
import { getFlagsForChunk, mapChunkFlagToSlotMarker, useSlotCommandButtons, useSlotToggleCommands } from "hooks/chunkSlots";
import { useDgIcons } from "hooks/dgIcons";
import { useAddBinding, useRemoveBinding } from "hooks/reports";
import { useFlagBinding } from "hooks/FlagBinding";
import { useAddExampleChunkMutation, useRemoveExampleChunkMutation, selectExampleChunkByEntityId, createExampleChunkEntityId } from "store/frontend/frontendSlice";
import { BindingProviderId, ReportViewContext } from "networking/reportsApi";
import { useTextEditCommand } from "hooks/TextEditCommand";
import { hasDiligenceFlag } from "utils/DiligenceReportUtils";
import { isSpanIdAddedChunk } from "store/dgml/dgmlTypes";
import { createFolderId } from "components/file_management/file_helpers";
import { useExpandedRichTextCommand } from "hooks/ExpandedRichTextCommand";
import { useRichText } from "hooks/chunkText";
import { CardRichTextContent } from "./CardRichTextContent";
import { ExpandedRichTextModal } from "./ExpandedRichTextModal";
import { CardTextClassName, CardTextContent } from "./CardTextContent";
import { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { selectClusterId, selectIsDocumentPublished, selectIsReportSchemaLoaded, selectReportContext, selectReportDocumentId, selectReportEntry, selectReportId } from "store/reports/selectors";
import { selectSelectedChunkInstance } from "store/sdvSelectors";
import { chunkSelectedColor } from "components/shell/presentation/AppStyles";
import { exampleChunksDocsetSelector } from "store/frontend/selectors";
import { getChunkLabel } from "hooks/LabelChunk";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import { ConfirmationDialog } from "components/shell/utils/ConfirmationDialog";
import { isValidationError } from "networking/api";
import { useQualityControlWarning } from "hooks/qualityControlWarning";

interface DropInfo {
    item?: DndChunk;
    isOver: boolean;
    canDrop: boolean;
    isDragging: boolean;
}

export interface ChunkItemSlotProps {
    item: ChunkSlotItem;
    styles?: ItemSlotStyles;
    canDrop?: (dropItem: DndChunk) => boolean;
    onDrop?: (item: DndChunk) => void;
    onClick?: () => void;
    onRemove?: () => void;
    hideManagementCommands?: boolean;
}

export interface ChunkItemSlotSubProps extends ChunkItemSlotProps, ItemSlotProps {
    canDrop: undefined;
    onDrop: undefined;
}

export const ChunkItemSlot = (props: ChunkItemSlotProps): JSX.Element => {
    const { onRemove } = props;

    const dgml = useDocumentDgml();
    const selectedChunkInstance = useAppSelector(selectSelectedChunkInstance);

    const chunkInstance = useMemo(() => dgml.isInitialized() ? dgml.getInstanceById(props.item.spanId ?? '') : undefined, [dgml, props.item.spanId]);
    const isSelected = !!chunkInstance && chunkInstance.spanId === selectedChunkInstance?.spanId;
    const html = useRichText(chunkInstance?.spanId);
    const dispatch = useAppDispatch();
    const chunkLabel = !!chunkInstance ? dispatch(getChunkLabel(chunkInstance)) : undefined;
    const expandedRichTextCommand = useExpandedRichTextCommand({ richText: html, label: chunkLabel });
    const clearCommand = useMemo(() => !onRemove || !chunkInstance?.spanId ? undefined :
        ({
            canExecute: () => true,
            execute: async () => onRemove?.(),
            primary: true
        }), [chunkInstance?.spanId, onRemove]);
    const commands = useSlotCommandButtons({
        onExpand: expandedRichTextCommand.isVisible ? expandedRichTextCommand.onClick : undefined,
        clear: clearCommand
    });

    const isClickable = !!props.onClick;
    const styles = useMemo(() => {
        const rootStyles: string[] = props.styles?.root || [];

        rootStyles.push(UnconfirmedClassName);

        if (!isSelected && isClickable) {
            rootStyles.push(SelectableClassName);
        }

        if (!!props.item.isDisabled) {
            rootStyles.push(DisabledClassName);
        }

        const styles: ItemSlotStyles = {
            root: rootStyles
        }

        return styles;
    }, [isSelected, props.item.isDisabled, props.styles?.root, isClickable])

    if (!dgml.isInitialized()) {
        return (
            <ItemSlot hideManagementCommands={true}>
                <Shimmer />
            </ItemSlot>
        )
    }

    return (
        <ItemSlot
            id={props.item.id}
            styles={styles}
            showCommandsList={true}
            commands={commands}
            onClick={!isSelected ? props.onClick : undefined}>
            {!!chunkInstance ?
                <>
                    <CardRichTextContent text={html} background={isSelected ? chunkSelectedColor : undefined} />
                    <ExpandedRichTextModal command={expandedRichTextCommand} />
                </> :
                <CardTextContent text="Empty" styles={{ text: CardTextClassName.Empty }} background={isSelected ? chunkSelectedColor : undefined} />
            }
        </ItemSlot>
    )
}

export interface ReportChunkItemSlotProps {
    item: ReportChunkSlotItem;
    styles?: ItemSlotStyles;
    canDrop?: (dropItem: DndChunk) => boolean;
    onDrop?: (item: DndChunk) => void;
    onClick?: () => void;
    hideManagementCommands?: boolean;
}

interface ErrorInfo {
    title: string;
    message: string;
}

export const ReportChunkItemSlot = (props: ReportChunkItemSlotProps): JSX.Element => {
    const [errorInfo, setErrorInfo] = useState<ErrorInfo | undefined>(undefined);

    const { binding, entryId } = props.item;
    const reportId = useAppSelector(selectReportId);
    const folderId = useAppSelector(selectReportDocumentId) ?? '';
    const clusterId = useAppSelector(selectClusterId);
    const entry = useAppSelector(state => selectReportEntry(state, entryId));
    const isSchemaLoaded = useAppSelector(selectIsReportSchemaLoaded);
    const context = useAppSelector(selectReportContext);
    const isDocumentPublished = useAppSelector(state => selectIsDocumentPublished(state, folderId));

    const dgml = useDocumentDgml();
    const selectedChunkInstance = useAppSelector(selectSelectedChunkInstance);

    const isSelected = !!binding?.spanId && selectedChunkInstance?.spanId === binding?.spanId;

    const isLoading = !dgml.isInitialized() || !isSchemaLoaded;

    const { trigger: triggerAddBinding } = useAddBinding(reportId);
    const { trigger: triggerRemoveBinding } = useRemoveBinding(reportId);
    const triggerFlagBinding = useFlagBinding();
    const [triggerAddExampleChunk, addExampleChunkState] = useAddExampleChunkMutation();
    const [triggerRemoveExampleChunk, removeExampleChunkState] = useRemoveExampleChunkMutation();
    const { data: exampleChunksState } = useAppSelector(state => exampleChunksDocsetSelector(clusterId, false)(state));

    const exampleChunkEntityId = useMemo(() => !!entry?.title && !!binding?.spanId ? createExampleChunkEntityId(entry.title, binding.spanId) : undefined, [entry?.title, binding?.spanId]);
    const exampleChunk = !!exampleChunkEntityId && !!exampleChunksState ? selectExampleChunkByEntityId(exampleChunksState, exampleChunkEntityId) : undefined;

    const chunkInstance = useMemo(() => {
        const chunk = isLoading ? null : dgml.getInstanceById(binding?.spanId ?? '');
        return chunk ?? undefined;
    }, [dgml, isLoading, binding?.spanId]);

    const confirmCommand = useMemo(() => context === ReportViewContext.Review ?
        ({
            canExecute: () => !binding?.userConfirmed,
            execute: async () => {
                await triggerAddBinding({
                    folderId: folderId,
                    entryId: entryId,
                    instanceId: binding?.spanId
                })
            }
        }) :
        undefined,
        [entryId, folderId, binding?.spanId, binding?.userConfirmed, context, triggerAddBinding]
    );

    const clearCommand = useMemo(() =>
    ({
        canExecute: () => !!binding?.spanId && !!context,
        execute: async () => {
            await triggerRemoveBinding({
                folderId: folderId,
                entryId: entryId,
                instanceId: binding?.spanId!
            });
        }
    }),
        [context, folderId, entryId, binding?.spanId, triggerRemoveBinding]
    );

    const textEditCommand = useTextEditCommand(clusterId, chunkInstance);

    const flagCommand = useMemo(() =>
    ({
        canExecute: () => true,
        execute: () => triggerFlagBinding(reportId, entryId, folderId, binding?.spanId, binding?.metadata)
    }),
        [reportId, folderId, entryId, binding?.spanId, binding?.metadata, triggerFlagBinding]
    );

    const exampleChunkCommand = useMemo(() => !isDocumentPublished ?
        ({
            canExecute: () => !!binding?.spanId && !!entry?.title && !isSpanIdAddedChunk(binding.spanId) && !!exampleChunksState && !addExampleChunkState.isLoading && !removeExampleChunkState.isLoading,
            execute: async () => {
                if (!!exampleChunk) {
                    await triggerRemoveExampleChunk(exampleChunk)
                }
                else {
                    await triggerAddExampleChunk({
                        docsetId: clusterId,
                        fileId: createFolderId(folderId).fileId,
                        label: entry?.title!,
                        spanId: binding?.spanId!
                    })
                }
            },
            hidden: !binding?.spanId || !exampleChunksState
        }) :
        undefined,
        [clusterId, folderId, entry?.title, binding?.spanId, exampleChunk, triggerRemoveExampleChunk, triggerAddExampleChunk, addExampleChunkState.isLoading, removeExampleChunkState.isLoading, exampleChunksState, isDocumentPublished]
    );

    const warning = useQualityControlWarning(entry?.title, binding?.spanId);
    const isFlagged = hasDiligenceFlag(binding?.metadata);
    const toggleCommands = useSlotToggleCommands({
        flag: flagCommand,
        isFlagged: isFlagged
    });

    const { icon } = useDgIcons();

    const [dropInfo, dropRef] = useDrop<DndChunk, void, DropInfo>(() => ({
        accept: DndType.Chunk,
        canDrop: item => !!props.canDrop?.(item as DndChunk),
        collect: m => ({
            item: m.getItem(),
            isOver: m.isOver(),
            canDrop: m.canDrop(),
            isDragging: !!m.getItem()
        }),
        drop: (item, m) => {
            if (!m.didDrop()) {
                props.onDrop?.(item);
            }
        },
    }), [props.canDrop, props.onDrop]);

    const targetChunk = (dropInfo.canDrop && dropInfo.isOver && !props.item.isDisabled ? dropInfo.item?.instance : chunkInstance) || chunkInstance;
    const html = useRichText(targetChunk?.spanId);

    const expandedRichTextCommand = useExpandedRichTextCommand({ richText: html, label: entry?.title });
    const commands = useSlotCommandButtons({
        clear: !props.hideManagementCommands ? clearCommand : undefined,
        edit: !props.hideManagementCommands ? textEditCommand : undefined,
        onExpand: expandedRichTextCommand.isVisible ? expandedRichTextCommand.onClick : undefined,
        showLabel: true,
        example: exampleChunkCommand,
        isExample: !!exampleChunk
    });

    const styles = useMemo(() => {
        const rootStyles: string[] = props.styles?.root || [];
        if (!binding?.isConfirmed) {
            rootStyles.push(UnconfirmedClassName);
        }

        if (!isSelected) {
            rootStyles.push(SelectableClassName);
        }

        if (props.item.isLinkable) {
            if (dropInfo.isDragging) {
                if (dropInfo.isOver && dropInfo.canDrop) {
                    rootStyles.push(ActiveDropTargetStyle);
                }
                else if (!dropInfo.canDrop) {
                    rootStyles.push(DisabledClassName);
                }
            }
            else {
                rootStyles.push(LinkableClassName);
            }
        }

        if (!!props.item.isDisabled) {
            rootStyles.push(DisabledClassName);
        }

        const styles: ItemSlotStyles = {
            root: rootStyles
        }

        return styles;
    }, [isSelected, binding?.isConfirmed, props.item.isLinkable, dropInfo, props.item.isDisabled, props.styles?.root])

    const markers = useMemo(() => !!targetChunk ? getFlagsForChunk(targetChunk, !!exampleChunk).flatMap(x => mapChunkFlagToSlotMarker(x, icon) || []) : undefined, [targetChunk, exampleChunk, icon])

    const isValidChunk = !!chunkInstance;
    const child = useMemo(() => isValidChunk ?
        <CardRichTextContent text={html} background={isSelected ? chunkSelectedColor : undefined} /> :
        <CardTextContent text="Empty" styles={{ text: CardTextClassName.Empty }} background={isSelected ? chunkSelectedColor : undefined} />, [html, isSelected, isValidChunk]);

    useEffect(() => {
        if (!!(addExampleChunkState.error as FetchBaseQueryError)) {
            const error = addExampleChunkState.error as FetchBaseQueryError;
            if (isValidationError(error.data) && !!error.data.errors["maxExamplesExceeded"]) {
                setErrorInfo({
                    title: "Uh oh!",
                    message: "You can have at most 10 examples per label. Please remove an existing example before adding a new one."
                });
            }
        }
    }, [addExampleChunkState.error]);

    if (isLoading) {
        return (
            <ItemSlot hideManagementCommands={true}>
                <Shimmer />
            </ItemSlot>
        )
    }

    return (
        <div ref={dropRef}>
            <ItemSlot
                id={props.item.id}
                isUserBindingProvided={binding?.bindingProviderId === BindingProviderId.User}
                styles={styles}
                confirm={confirmCommand}
                isConfirmed={!!binding?.isConfirmed}
                showCommandsList={isSelected}
                commands={commands}
                toggles={toggleCommands}
                warning={warning?.reasoning}
                onClick={!isSelected ? props.onClick : undefined}
                markers={markers}
                children={child} />
            <ExpandedRichTextModal command={expandedRichTextCommand} />
            <ConfirmationDialog
                hidden={!errorInfo}
                title={errorInfo?.title}
                subText={errorInfo?.message}
                confirmButtonText="OK"
                dismissedCallback={() => setErrorInfo(undefined)}
                confirmedCallback={() => setErrorInfo(undefined)} />
        </div>
    )
}
